import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  signal
} from '@angular/core';
import {PageTitleComponent} from "../../../components/page-title/page-title.component";
import {ToggleSwitch, ToggleSwitchChangeEvent} from "primeng/toggleswitch";
import {FormBuilder, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {NgClass} from "@angular/common";
import {firstValueFrom} from "rxjs";
import {Divider} from "primeng/divider";
import {isNative, platform} from "../../../utils";
import {Dialog} from "primeng/dialog";
import {Button} from "primeng/button";
import {SubmitButtonComponent} from "../../../components/submit-button/submit-button.component";
import {FloatLabel} from "primeng/floatlabel";
import {InputMask} from "primeng/inputmask";
import {InputText} from "primeng/inputtext";
import {SkeletonModule} from "primeng/skeleton";
import {PushNotificationService} from "../../../services/push-notification.service";
import {takeUntilDestroyed, toSignal} from "@angular/core/rxjs-interop";
import {NotificationSettingsService} from "../../../services/notification-settings.service";
import {NotificationSettingsDto} from "homey-housing-lottery-app-dto";
import {environment} from "../../../../../environments/environment";
import {Injector} from "@angular/core";

@Component({
  imports: [
    PageTitleComponent,
    ToggleSwitch,
    FormsModule,
    Divider,
    ReactiveFormsModule,
    NgClass,
    Dialog,
    Button,
    SubmitButtonComponent,
    FloatLabel,
    InputMask,
    InputText,
    SkeletonModule
  ],
  templateUrl: './account-notifications-page.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AccountNotificationsPageComponent {
  private readonly fb = inject(FormBuilder);
  protected readonly pushNotificationService = inject(PushNotificationService)
  private readonly notificationSettingsService = inject(NotificationSettingsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  protected readonly devicePushNotificationsEnabled = toSignal(
    this.pushNotificationService.devicePushPermissionsEnabled$,
    {initialValue: false}
  );
  private readonly phoneValidatorFn = (control: {value: string | null | undefined}) =>
    this.isValidPhone(control.value) ? null : {phoneInvalid: true};

  protected readonly form = this.fb.group({
    emailNotificationsEnabled: this.fb.nonNullable.control<boolean | undefined>(undefined),
    pushNotificationsEnabled: this.fb.nonNullable.control<boolean | undefined>(undefined),
    nonLotteryNotificationsEnabled: this.fb.nonNullable.control<boolean | undefined>(undefined),
    smsNotificationsEnabled: this.fb.nonNullable.control<boolean | undefined>(undefined),
    email: this.fb.control<string | undefined>(undefined, [Validators.required, Validators.email]),
    phone: this.fb.control<string | undefined>(undefined, [Validators.required, this.phoneValidatorFn])
  })

  private smsDialogOpenedFromToggle = false;

  private readonly syncPushToggleEffect = effect(() => {
    const settings = this.settings();
    if (!settings) {
      return;
    }

    const pushChannel = settings.channels.push;
    const pushEnabledOnDevice = this.devicePushNotificationsEnabled();
    const shouldEnablePushOnDevice = Boolean(pushChannel?.enabled && pushEnabledOnDevice);
    this.form.patchValue({
      pushNotificationsEnabled: shouldEnablePushOnDevice
    }, {emitEvent: false});
  }, {injector: this.injector});

  protected readonly loading = signal(true);
  protected readonly settings = signal<NotificationSettingsDto | null>(null);
  protected readonly emailPopupVisible = signal(false);

  submitPhoneNumber = async () => {
    const phoneControl = this.form.controls.phone;
    phoneControl.markAsTouched();
    phoneControl.updateValueAndValidity();
    if (phoneControl.invalid) {
      return;
    }

    const phoneNr = phoneControl.value ?? '';
    await firstValueFrom(this.notificationSettingsService.updateChannel({
      type: 'text',
      enabled: true,
      phoneNr
    }));

    await this.refreshNotificationSettings();

    this.smsDialogOpenedFromToggle = false;
    this.enableSmsPopupVisible.set(false);
    this.editSmsPopupVisible.set(false);
  }

  submitEmailAddress = async () => {
    const emailControl = this.form.controls.email;
    emailControl.markAsTouched();
    emailControl.updateValueAndValidity();
    if (emailControl.invalid) {
      return;
    }

    const email = (emailControl.value ?? '').trim().toLowerCase();
    await firstValueFrom(this.notificationSettingsService.updateChannel({
      type: 'email',
      enabled: true,
      emailAddress: email
    }));

    await this.refreshNotificationSettings();
    this.emailPopupVisible.set(false);
  }

  protected openEmailEdit() {
    this.emailPopupVisible.set(true);
  }

  protected openPhoneEdit() {
    this.smsDialogOpenedFromToggle = false;
    this.form.controls.phone.markAsUntouched();
    this.form.controls.phone.updateValueAndValidity({emitEvent: false});
    this.editSmsPopupVisible.set(true);
  }

  protected sendSmsNotificationEnabled(event: ToggleSwitchChangeEvent) {
    if (event.checked) {
      this.smsDialogOpenedFromToggle = true;
      this.form.controls.phone.markAsUntouched();
      this.form.controls.phone.updateValueAndValidity({emitEvent: false});
      this.enableSmsPopupVisible.set(true);
    } else {
      void this.updateTextChannel(false);
    }
  }

  async onPushNotionEnabled(event: ToggleSwitchChangeEvent) {
    await this.updatePushChannel(Boolean(event.checked));
  }

  protected onHide() {
    if (this.smsDialogOpenedFromToggle) {
      this.form.patchValue({
        smsNotificationsEnabled: false
      }, {emitEvent: false});
    }
    this.smsDialogOpenedFromToggle = false;
    this.enableSmsPopupVisible.set(false);
  }

  protected readonly enableSmsPopupVisible = signal(false);
  protected readonly editSmsPopupVisible = signal(false);

  protected readonly isNative = isNative;
  protected readonly platform = platform;
  protected readonly pushPermissionDeniedDialogVisible = signal(false);

  constructor() {
    this.loadNotificationSettings();
    this.setupControlSubscriptions();
  }

  protected readonly iosAppUrl = environment.appstoreLinks.ios;
  protected readonly androidAppUrl = environment.appstoreLinks.android;

  private loadNotificationSettings() {
    this.loading.set(true);
    void this.refreshNotificationSettings();
  }

  private async refreshNotificationSettings() {
    try {
      const latest = await firstValueFrom(this.notificationSettingsService.getNotificationSettings());
      this.applySettings(latest);
    } catch (error) {
      console.error('Failed to load notification settings', error);
    } finally {
      this.loading.set(false);
    }
  }

  private applySettings(settings: NotificationSettingsDto) {
    this.settings.set(settings);

    const emailChannel = settings.channels.email;
    const textChannel = settings.channels.text;
    const pushChannel = settings.channels.push;

    const pushEnabledOnDevice = this.devicePushNotificationsEnabled();
    const shouldEnablePushOnDevice = Boolean(pushChannel?.enabled && pushEnabledOnDevice);

    this.form.patchValue({
      emailNotificationsEnabled: emailChannel?.enabled ?? false,
      smsNotificationsEnabled: textChannel?.enabled ?? false,
      nonLotteryNotificationsEnabled: settings.categories.promotional ?? false,
      pushNotificationsEnabled: shouldEnablePushOnDevice,
      email: emailChannel?.emailAddress ?? '',
      phone: textChannel?.phoneNr ?? ''
    }, {emitEvent: false});

    const pushControl = this.form.controls.pushNotificationsEnabled;
    if (!isNative) {
      pushControl.disable({emitEvent: false});
    } else {
      pushControl.enable({emitEvent: false});
    }
  }

  private setupControlSubscriptions() {
    this.form.controls.emailNotificationsEnabled.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        if (value === null || value === undefined) return;
        void this.updateEmailChannel(value);
      });

    this.form.controls.nonLotteryNotificationsEnabled.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        if (value === null || value === undefined) return;
        void this.updatePromotionalPreference(value);
      });

    this.form.controls.phone.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const control = this.form.controls.phone;
        if (!control.touched) return;
        control.updateValueAndValidity({emitEvent: false});
      });

  }

  private async updateEmailChannel(enabled: boolean) {
    const emailAddress = this.form.controls.email.value;
    if (!emailAddress) {
      return;
    }

    await firstValueFrom(this.notificationSettingsService.updateChannel({
      type: 'email',
      enabled,
      emailAddress: emailAddress.toLowerCase(),
    }));

    await this.refreshNotificationSettings();
  }

  private async updateTextChannel(enabled: boolean) {
    const settings = this.settings();
    if (!settings) {
      return;
    }

    const textChannel = settings.channels.text;
    const phoneNr = this.form.controls.phone.value ?? textChannel?.phoneNr ?? '';
    if (!phoneNr) {
      return;
    }

    await firstValueFrom(this.notificationSettingsService.updateChannel({
      type: 'text',
      enabled,
      phoneNr,
    }));

    await this.refreshNotificationSettings();
  }

  private async updatePushChannel(enabled: boolean): Promise<boolean> {
    const settings = this.settings();
    if (!settings) {
      return false;
    }

    if (enabled) {
      const result = await this.pushNotificationService.registerNotifications();
      if (!result.granted) {
        this.form.patchValue({pushNotificationsEnabled: false}, {emitEvent: false});
        if (result.status === 'denied') {
          this.pushPermissionDeniedDialogVisible.set(true);
        }
        return false;
      }
    }

    await firstValueFrom(this.notificationSettingsService.updateChannel({
      type: 'push',
      enabled,
      token: undefined
    }));

    await this.refreshNotificationSettings();
    return true;
  }

  private async updatePromotionalPreference(enabled: boolean) {
    const settings = this.settings();
    if (!settings) {
      return;
    }

    await firstValueFrom(this.notificationSettingsService.updatePreference({
      category: 'promotional',
      enabled,
    }));

    await this.refreshNotificationSettings();
  }

  private isValidEmail(email: string | null | undefined) {
    if (!email) return false;
    return /\S+@\S+\.\S+/.test(email);
  }

  private isValidPhone(phone: string | null | undefined) {
    if (!phone) {
      return false;
    }
    // Accept 10 digits regardless of mask formatting
    const digits = phone.replace(/\D/g, '');
    return digits.length === 10;
  }
}
